import { useEffect, useState, useSyncExternalStore } from 'react'
import { useOmniAuth } from '@omni/auth'
import { useNavLayout, useToast } from '@omni/ui'
import type { ToastVariant } from '@omni/ui'
import { Icon as OmniIcon } from '@omni/ui/icons'
import {
  ChevronDown,
  ChevronRight,
  LogOut,
  Maximize2,
  MoreHorizontal,
  Menu,
  Minimize2,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  X,
} from 'lucide-react'
import {
  DEFAULT_SURFACE_PATH,
  disabledSurfaceForPathname,
  railIdFor,
  surfaceForPathname,
  useRailSurfaces,
  useSurfaceVisibility,
  useVisibleSurfaces,
  type Surface,
} from '../surfaces'
import { productName } from '../lib/config'

// A product with its own name ("Nateva Intelligence") wraps onto two lines in
// the menu rather than being cut off; "<Name> Command Centre" keeps one line.
const NAME_CLASS = /command cent(er|re)/i.test(productName) ? 'truncate' : 'line-clamp-2 break-words'
import { activeInstance } from '../instances'
import { shellNavigate, usePathname } from '../lib/navigation'
import { shellEvents } from '../lib/events'
import { useShellStore } from '../lib/store'
import { SurfaceHost } from '../hosts/SurfaceHost'
import { CommandPalette } from './CommandPalette'
import { DisabledModule } from './DisabledModule'
import { ClaudeCornerButton, ClaudeHost, ClaudeRailButton, openClaudePanel } from '../claude/ClaudePanel'
import { PeekDrawer } from './PeekDrawer'
import { Splash } from './Splash'
import { SampleDataFrame } from '../proservices/SampleDataStrip'
import { GreatLeadBell } from '../leads/GreatLeadBell'
import { FreshnessPill } from './FreshnessPill'
import type { FreshnessSource } from '../data/freshness'
import * as surfaceAppsModule from '@shell-surface-apps'
import { AppSwitcher } from '@shell-front-door'

// Live pro-services tenants that may carry the preview's sample rows until
// their mailbox is connected: every page gets the "sample data" strip while
// any are there (proservices/SampleDataStrip.tsx). Build-time, so every other
// build is unchanged.
const SAMPLE_STRIP = ['asdm-solution', 'elevate-wealth'].includes(import.meta.env.VITE_INSTANCE_SLUG ?? '')

// The great-lead bell (leads/GreatLeadBell.tsx) for instances that turn it on.
const LEAD_BELL = activeInstance.leadAlerts === true && activeInstance.modules.includes('leads')

// The data pill in the top bar (data/freshness.ts) for instances that turn it
// on: the sources ride in the build's own surface-apps module, so a build
// without them shows nothing and every other build is unchanged.
const FRESHNESS_SOURCES: FreshnessSource[] | undefined =
  activeInstance.freshnessPill === true
    ? ((surfaceAppsModule as Record<string, unknown>).freshnessSources as FreshnessSource[] | undefined)
    : undefined
// Instances on the People contract export a bar for "See it as" (team/PeopleBar.tsx).
const PeopleBar = (surfaceAppsModule as Record<string, unknown>).PeopleBar as
  | ((p: { surfaceId?: string }) => JSX.Element | null)
  | undefined
const FRESHNESS_DATA_PATH = activeInstance.modules.includes('data') ? '/your-data' : undefined
// The notifications bell (instance.notifications) from the build's own
// surface-apps module; every build without both shows nothing and is unchanged.
const NotificationBell =
  activeInstance.notifications === true
    ? ((surfaceAppsModule as Record<string, unknown>).NotificationBell as
        | ((p: { variant: 'panel' | 'rail' | 'phone' | 'top'; onNavigate?: () => void }) => JSX.Element | null)
        | undefined)
    : undefined
// Ask Claude and the bell as small icons top right instead of in the menu
// (instance.cornerIcons; MBA Brands first, Sam, 30 Oct 2026).
const CORNER_ICONS = activeInstance.cornerIcons === true

const LEVEL_TO_VARIANT: Record<string, ToastVariant> = {
  info: 'default',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
}

export function Chrome() {
  const pathname = usePathname()
  const surface = surfaceForPathname(pathname)
  // A path that belongs to a real-but-gated-off module renders the branded
  // "not in your workspace" panel instead of redirecting (no-silent-null).
  const disabledSurface = surface ? undefined : disabledSurfaceForPathname(pathname)
  // A tab this person may not open (per-person visibility, e.g. MBA's People
  // tab for a brand manager): a plain note, never the page.
  const canSee = useSurfaceVisibility()
  const blocked = surface ? canSee(surface.meta.id) === false : false
  const visibleSurfaces = useVisibleSurfaces()
  const setPaletteOpen = useShellStore((s) => s.setPaletteOpen)
  const openContextCard = useShellStore((s) => s.openContextCard)
  const { toggleFocus } = useNavLayout()
  const { toast } = useToast()
  const isPhone = useIsPhone()

  // '/' (or any unknown path) lands on the default surface.
  useEffect(() => {
    if (!surface && !disabledSurface) shellNavigate(DEFAULT_SURFACE_PATH)
  }, [surface, disabledSurface, pathname])

  // Cmd/Ctrl+K opens the palette from anywhere; Cmd/Ctrl+\ drops both
  // navigations into (or out of) focus mode without reaching for the rail.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(true)
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault()
        toggleFocus()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [setPaletteOpen, toggleFocus])

  // Bus wiring: peek-* → drawer, open-customer / draft-email-to → navigate,
  // notification → toast. (create-task-from-thread / create-project-from-
  // customer are consumed by the surfaces themselves.)
  useEffect(() => {
    const unsubs = [
      shellEvents.subscribe('peek-customer', (e) =>
        openContextCard({ kind: 'customer', id: e.payload.customerId }),
      ),
      shellEvents.subscribe('peek-contact', (e) =>
        openContextCard({
          kind: 'contact',
          id: e.payload.contactId,
          namespace: e.payload.from === 'mail' ? 'mail' : 'crm',
        }),
      ),
      shellEvents.subscribe('peek-thread', (e) =>
        openContextCard({ kind: 'thread', id: e.payload.threadId }),
      ),
      shellEvents.subscribe('peek-task', (e) =>
        openContextCard({ kind: 'task', id: e.payload.taskId }),
      ),
      shellEvents.subscribe('peek-project', (e) =>
        openContextCard({ kind: 'project', id: e.payload.projectId }),
      ),
      shellEvents.subscribe('open-customer', (e) =>
        shellNavigate(`/crm/companies/${e.payload.customerId}`),
      ),
      shellEvents.subscribe('draft-email-to', (e) => {
        const qs = new URLSearchParams()
        if (e.payload.email) qs.set('to', e.payload.email)
        if (e.payload.subject) qs.set('subject', e.payload.subject)
        if (e.payload.body) qs.set('body', e.payload.body)
        const query = qs.toString()
        shellNavigate(`/mail/compose${query ? `?${query}` : ''}`)
      }),
      shellEvents.subscribe('notification', (e) =>
        toast(e.payload.title, {
          description: e.payload.body,
          variant: LEVEL_TO_VARIANT[e.payload.level ?? 'info'] ?? 'default',
        }),
      ),
    ]
    return () => unsubs.forEach((u) => u())
  }, [openContextCard, toast])

  const page =
        surface && blocked ? (
          <div className="flex h-full items-center justify-center p-8">
            <div className="max-w-md text-center">
              <p className="text-base font-semibold">{surface.meta.label} is not part of your access</p>
              <p className="mt-2 text-sm text-text-secondary">
                Ask the person who runs your {productName} logins if you need it.
              </p>
              <button
                type="button"
                onClick={() => shellNavigate(DEFAULT_SURFACE_PATH)}
                className="mt-4 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-2"
              >
                Go to the start page
              </button>
            </div>
          </div>
        ) : surface ? (
          SAMPLE_STRIP ? (
            <SampleDataFrame>
              <SurfaceHost key={surface.meta.id} surface={surface} />
            </SampleDataFrame>
          ) : (
            <SurfaceHost key={surface.meta.id} surface={surface} />
          )
        ) : disabledSurface ? (
          <DisabledModule surface={disabledSurface} />
        ) : (
          <Splash label={`Opening ${productName}…`} />
        )

  return (
    <div
      className={
        isPhone
          ? 'flex h-[100dvh] w-screen flex-col overflow-hidden bg-surface-0 text-text-primary'
          : 'flex h-screen w-screen overflow-hidden bg-surface-0 text-text-primary'
      }
    >
      {isPhone ? (
        <PhoneNav activeId={railIdFor(surface?.meta.id)} pathname={pathname} />
      ) : (
        <IconRail activeId={railIdFor(surface?.meta.id)} />
      )}
      <main
        className={
          (FRESHNESS_SOURCES && !isPhone) || PeopleBar
            ? 'relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden'
            : 'relative min-h-0 min-w-0 flex-1 overflow-hidden'
        }
      >
        {CORNER_ICONS && !isPhone && (
          <div className="absolute right-6 top-3 z-30 flex items-center gap-0.5 rounded-full border border-border bg-surface-1 p-0.5 shadow-sm">
            <ClaudeCornerButton onOpen={openClaudePanel} />
            {NotificationBell && <NotificationBell variant="top" />}
          </div>
        )}
        {PeopleBar && <PeopleBar surfaceId={surface?.meta.id} />}
        {FRESHNESS_SOURCES && !isPhone && (
          <div className="flex h-11 shrink-0 items-center justify-between gap-3 border-b border-border bg-surface-1 px-4">
            <span className="truncate text-[13px] text-text-secondary">{surface?.meta.label ?? ''}</span>
            <FreshnessPill sources={FRESHNESS_SOURCES} dataPath={FRESHNESS_DATA_PATH} />
          </div>
        )}
        {(FRESHNESS_SOURCES && !isPhone) || PeopleBar ? (
          <div className="min-h-0 flex-1 overflow-hidden">{page}</div>
        ) : (
          page
        )}
      </main>
      <CommandPalette />
      <ClaudeHost surfaceId={surface?.meta.id} surfaceLabel={surface?.meta.label} screens={visibleSurfaces.filter((x) => x.meta.id !== 'learn').map((x) => x.meta.label)} />
      <PeekDrawer />
    </div>
  )
}

/**
 * Per-module accent colours — mirrors the Omni-Shell / OmniSwitcher registry
 * (mail=purple, crm=pink, projects=gold, marketing=emerald, finance=teal,
 * portal=blue, growth=rose) so the rail reads like the old suite switcher.
 */
const SURFACE_ICON_COLOR: Record<string, string> = {
  home: 'text-brand-amber',
  mail: 'text-brand-purple',
  crm: 'text-brand-pink',
  projects: 'text-brand-amber',
  marketing: 'text-emerald-500',
  finance: 'text-teal-500',
  portal: 'text-blue-500',
  growth: 'text-rose-500',
  connections: 'text-text-secondary',
  ops: 'text-orange-500',
}

// The rail opens fully labelled and stays that way until Sam says otherwise —
// the Mortgage Man got it first (Aaron via Sam, 3 Sep 2026) and it became the
// product default on 6 Sep 2026 ("far left navigation always fully expanded, so
// you can actually see the items"). What changed on 8 Sep 2026 is that it is no
// longer a build-time constant: on an iPad the shell rail plus the module's own
// sidebar eat half the screen, so both now collapse to icon strips, together via
// Focus or one at a time. Expanded is still the default every session starts in.

// Phones (below 768px) get no side rail: a 240px menu on a 390px screen left
// every page about 150px to work with (ONA, 9 Oct 2026). A slim top bar carries
// a menu button that opens the same labelled menu as an overlay drawer, which
// closes on any navigation. Desktop and iPad keep the rail exactly as before
// (ONA's rule from 7 Sep: never an icon-only rail on desktop).
const PHONE_QUERY = '(max-width: 767px)'

function subscribePhone(onChange: () => void) {
  const mq = window.matchMedia(PHONE_QUERY)
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}

function useIsPhone(): boolean {
  return useSyncExternalStore(
    subscribePhone,
    () => window.matchMedia(PHONE_QUERY).matches,
    () => false,
  )
}

function BrandMark({ small = false }: { small?: boolean }) {
  const logoUrl = activeInstance.theme.logoUrl
  const box = small ? 'h-8 w-8' : 'h-9 w-9'
  if (logoUrl && activeInstance.theme.logoBackground) {
    // White-artwork marks need their branded ground; contain, don't crop.
    return (
      <span
        className={`flex ${box} shrink-0 items-center justify-center rounded-lg p-1`}
        style={{ background: activeInstance.theme.logoBackground }}
      >
        <img src={logoUrl} alt="" className="max-h-full max-w-full object-contain" />
      </span>
    )
  }
  if (logoUrl) return <img src={logoUrl} alt="" className="h-8 w-8 shrink-0" />
  return (
    <span
      className={`flex ${box} shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white`}
      style={{ background: 'var(--gradient-brand)' }}
    >
      {productName.charAt(0) || 'O'}
    </span>
  )
}

function PhoneNav({ activeId, pathname }: { activeId?: string; pathname: string }) {
  const [open, setOpen] = useState(false)
  const setPaletteOpen = useShellStore((s) => s.setPaletteOpen)

  // Any navigation (a menu tap, a link inside a page, back/forward) closes it.
  useEffect(() => {
    setOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open])

  return (
    <>
      <header className="flex h-12 shrink-0 items-center gap-1 border-b border-border bg-surface-1 px-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          aria-expanded={open}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-surface-2 hover:text-text-primary"
        >
          <Menu className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => shellNavigate(DEFAULT_SURFACE_PATH)}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1.5 py-1 text-left"
        >
          <BrandMark small />
          <span className="truncate text-sm font-semibold">{productName}</span>
        </button>
        {FRESHNESS_SOURCES && <FreshnessPill sources={FRESHNESS_SOURCES} variant="phone" dataPath={FRESHNESS_DATA_PATH} />}
        {LEAD_BELL && <GreatLeadBell variant="phone" />}
        {CORNER_ICONS && <ClaudeCornerButton onOpen={openClaudePanel} />}
        {NotificationBell && <NotificationBell variant="phone" />}
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          aria-label="Search"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-surface-2 hover:text-text-primary"
        >
          <Search className="h-5 w-5" />
        </button>
      </header>

      {open && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Menu">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="absolute inset-0 h-full w-full cursor-default bg-black/40"
          />
          <aside className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col overflow-y-auto border-r border-border bg-surface-1 px-3 py-3 shadow-xl">
            <RailPanel activeId={activeId} onClose={() => setOpen(false)} />
          </aside>
        </div>
      )}
    </>
  )
}

/**
 * The fully labelled menu: the desktop rail's expanded state, and the same menu
 * inside the phone drawer (where the corner button closes the drawer and Focus,
 * which only shrinks rails, is left out).
 */
function RailPanel({ activeId, onClose }: { activeId?: string; onClose?: () => void }) {
  const setPaletteOpen = useShellStore((s) => s.setPaletteOpen)
  const { signOut } = useOmniAuth()
  const { focusMode, toggleShell, toggleFocus } = useNavLayout()
  const { main, more } = useRailSurfaces()
  // "More" opens by itself while one of its pages is the one on screen.
  const moreActive = more.some((s) => s.meta.id === activeId)
  const [moreOpen, setMoreOpen] = useState(moreActive)
  useEffect(() => {
    if (moreActive) setMoreOpen(true)
  }, [moreActive])

  const navButton = ({ meta, Icon }: Surface) => {
    const active = meta.id === activeId
    return (
      <button
        key={meta.id}
        type="button"
        onClick={() => {
          shellNavigate(meta.basePath)
          // Re-tapping the page you are on is not a navigation; still close.
          onClose?.()
        }}
        className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-all duration-200 ${
          active
            ? 'relative bg-surface-2 font-medium text-text-primary before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-accent'
            : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary'
        }`}
      >
        <OmniIcon icon={Icon} size={16} className={`shrink-0 ${active ? 'text-text-primary' : ''}`} />
        <span className="truncate">{meta.label}</span>
      </button>
    )
  }

  return (
    <>
      <div className="mb-3 flex items-center gap-1">
        <button
          type="button"
          onClick={() => shellNavigate(DEFAULT_SURFACE_PATH)}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left transition-colors hover:bg-surface-2"
        >
          <BrandMark />
          <span className={`${NAME_CLASS} text-sm font-semibold leading-snug`}>{productName}</span>
        </button>
        {/* Collapse this rail on its own; Focus below takes both rails down.
            In the phone drawer the same corner closes the drawer. */}
        <button
          type="button"
          onClick={onClose ?? toggleShell}
          title={onClose ? 'Close menu' : 'Collapse menu'}
          aria-label={onClose ? 'Close menu' : 'Collapse menu'}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-2 hover:text-text-primary"
        >
          {onClose ? <X className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>
      </div>

      {/* The customer front door's app switcher: the front page and this
          person's other apps (front-door builds only, __FRONT_DOOR__). */}
      {__FRONT_DOOR__ && <AppSwitcher />}

      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        className="mb-2 flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-text-secondary transition-colors hover:bg-surface-2 hover:text-text-primary"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span>Search</span>
        {/* A kbd chip, optically centred against the label rather than a bare
            10px string riding the text baseline. */}
        {!onClose && (
          <kbd className="ml-auto self-center rounded border border-border bg-surface-0 px-1.5 py-0.5 font-sans text-[10px] leading-none text-text-muted">
            Ctrl K
          </kbd>
        )}
      </button>

      {!CORNER_ICONS && (
        <ClaudeRailButton
          collapsed={false}
          onOpen={() => {
            onClose?.()
            openClaudePanel()
          }}
        />
      )}

      {LEAD_BELL && <GreatLeadBell variant="panel" onNavigate={onClose} />}
      {NotificationBell && !CORNER_ICONS && <NotificationBell variant="panel" onNavigate={onClose} />}

      {/* One tap takes BOTH navigations down to icon strips — the shell rail
          here and the module's own sidebar next to it — and one tap brings
          them back. On an iPad that is ~300px of table you get to read. */}
      {!onClose && (
        <button
          type="button"
          onClick={toggleFocus}
          aria-pressed={focusMode}
          className="mb-2 flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-text-secondary transition-colors hover:bg-surface-2 hover:text-text-primary"
        >
          <Minimize2 className="h-4 w-4 shrink-0" />
          <span>Focus</span>
          <kbd className="ml-auto self-center rounded border border-border bg-surface-0 px-1.5 py-0.5 font-sans text-[10px] leading-none text-text-muted">
            Ctrl \
          </kbd>
        </button>
      )}

      <nav className="flex flex-1 flex-col gap-1">
        {main.map(navButton)}
        {more.length > 0 && (
          <div className="mt-2 flex flex-col gap-1 border-t border-border pt-2">
            <button
              type="button"
              onClick={() => setMoreOpen((o) => !o)}
              aria-expanded={moreOpen}
              className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-text-muted transition-colors hover:bg-surface-2 hover:text-text-primary"
            >
              <MoreHorizontal className="h-4 w-4 shrink-0" />
              <span>More</span>
              {moreOpen ? <ChevronDown className="ml-auto h-4 w-4" /> : <ChevronRight className="ml-auto h-4 w-4" />}
            </button>
            {moreOpen && more.map(navButton)}
          </div>
        )}
      </nav>

      <button
        type="button"
        onClick={() => void signOut()}
        className="mt-2 flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-text-secondary transition-colors hover:bg-surface-2 hover:text-danger"
      >
        <LogOut className="h-4 w-4 shrink-0" />
        <span>Sign out</span>
      </button>
    </>
  )
}

function IconRail({ activeId }: { activeId?: string }) {
  const setPaletteOpen = useShellStore((s) => s.setPaletteOpen)
  const { signOut } = useOmniAuth()
  const logoUrl = activeInstance.theme.logoUrl
  const { shellCollapsed, focusMode, toggleShell, toggleFocus } = useNavLayout()
  const { main, more } = useRailSurfaces()
  // The icon strip carries the main pages; "More" opens the full menu, unless
  // a More page is on screen, when it stays as an icon so you can see where you are.
  const surfaces = [...main, ...more.filter((s) => s.meta.id === activeId)]

  if (!shellCollapsed) {
    return (
      <aside className="flex w-60 shrink-0 flex-col border-r border-border bg-surface-1 px-3 py-3">
        <RailPanel activeId={activeId} />
      </aside>
    )
  }

  return (
    <aside className="flex w-14 shrink-0 flex-col items-center border-r border-border bg-surface-1 py-3">
      <button
        type="button"
        title={productName}
        aria-label={productName}
        onClick={() => shellNavigate(DEFAULT_SURFACE_PATH)}
        className="group/brand relative mb-3 flex h-9 w-9 items-center justify-center"
      >
        {logoUrl && activeInstance.theme.logoBackground ? (
          // White-artwork marks need their branded ground; contain, don't crop.
          <span
            className="flex h-9 w-9 items-center justify-center rounded-lg p-1"
            style={{ background: activeInstance.theme.logoBackground }}
          >
            <img src={logoUrl} alt={productName} className="max-h-full max-w-full object-contain" />
          </span>
        ) : logoUrl ? (
          <>
            <span aria-hidden className="brand-logo-halo" />
            <img src={logoUrl} alt={productName} className="h-8 w-8" />
          </>
        ) : (
          <span
            className="flex h-9 w-9 items-center justify-center rounded-lg text-sm font-bold text-white"
            style={{ background: 'var(--gradient-brand)' }}
          >
            {productName.charAt(0) || 'O'}
          </span>
        )}
      </button>

      <button
        type="button"
        title="Expand menu"
        aria-label="Expand menu"
        onClick={toggleShell}
        className="mb-1 flex h-9 w-9 items-center justify-center rounded-full text-text-muted transition-colors hover:bg-surface-2 hover:text-text-primary"
      >
        <PanelLeftOpen className="h-4 w-4" />
      </button>

      {/* In focus mode this is the way back out to both full navigations. */}
      <button
        type="button"
        title={focusMode ? 'Show both menus' : 'Focus (hide both menus)'}
        aria-label={focusMode ? 'Show both menus' : 'Focus mode'}
        aria-pressed={focusMode}
        onClick={toggleFocus}
        className={`mb-1 flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-surface-2 hover:text-text-primary ${
          focusMode ? 'text-accent' : 'text-text-muted'
        }`}
      >
        {focusMode ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
      </button>

      {__FRONT_DOOR__ && <AppSwitcher collapsed />}

      {!CORNER_ICONS && <ClaudeRailButton collapsed onOpen={openClaudePanel} />}

      {LEAD_BELL && <GreatLeadBell variant="rail" />}
      {NotificationBell && !CORNER_ICONS && <NotificationBell variant="rail" />}

      <button
        type="button"
        title="Search (Ctrl+K)"
        aria-label="Search"
        onClick={() => setPaletteOpen(true)}
        className="mb-2 flex h-9 w-9 items-center justify-center rounded-full text-text-muted transition-colors hover:bg-surface-2 hover:text-text-primary"
      >
        <Search className="h-4 w-4" />
      </button>

      <nav className="flex flex-1 flex-col items-center gap-1">
        {surfaces.map(({ meta, Icon }) => {
          const active = meta.id === activeId
          // The Mortgage Man's rail is monochrome-red: neutral inactive icons,
          // the shared active treatment on the current one — the per-module
          // Omni suite colours are that suite's identity, not the customer's.
          // Build-time constant, so every other build keeps the coloured rail.
          const inactiveColor =
            import.meta.env.VITE_INSTANCE_SLUG === 'mortgage-man'
              ? 'text-text-secondary'
              : (SURFACE_ICON_COLOR[meta.id] ?? 'text-text-secondary')
          return (
            <button
              key={meta.id}
              type="button"
              title={meta.label}
              aria-label={meta.label}
              onClick={() => shellNavigate(meta.basePath)}
              className={`flex h-9 w-9 items-center justify-center rounded-full transition-all duration-200 ${
                active
                  ? 'relative bg-surface-2 text-text-primary before:absolute before:-left-2 before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-accent'
                  : `hover:bg-surface-2 ${inactiveColor}`
              }`}
            >
              <OmniIcon icon={Icon} size={16} className={active ? 'text-text-primary' : undefined} />
            </button>
          )
        })}
        {more.length > 0 && (
          <button
            type="button"
            title="More"
            aria-label="More"
            onClick={toggleShell}
            className="flex h-9 w-9 items-center justify-center rounded-full text-text-muted transition-colors hover:bg-surface-2 hover:text-text-primary"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        )}
      </nav>

      <button
        type="button"
        title="Sign out"
        aria-label="Sign out"
        onClick={() => void signOut()}
        className="mt-2 flex h-9 w-9 items-center justify-center rounded-full text-text-muted transition-colors hover:bg-surface-2 hover:text-danger"
      >
        <LogOut className="h-4 w-4" />
      </button>
    </aside>
  )
}
